import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, FileText, BarChart3, CheckCircle, Info, Wand2, CloudCog, Goal, Users, RefreshCw } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import toast from 'react-hot-toast';
import ProjectContextDisplay from '../components/ProjectContextDisplay';
import { useComplianceScore } from '../hooks/useComplianceScore';
import AnalysisResults from '../components/Analysis/AnalysisResults';
import ConfidenceScoringTab from '../components/Analysis/ConfidenceScoringTab';
import SOPGenerationTab from '../components/Analysis/SOPGenerationTab';
import Tooltip from '../components/Analysis/Tooltip';
import LoadingModal from '../components/LoadingModal';
import EnhancedProcessSynthesisTab from '../components/Analysis/EnhancedProcessSynthesisWrapper';
import ToolbarDropdown from '../components/ProcessMaps/ToolbarDropdown';
import HelpTooltip from '../components/HelpTooltip';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';


interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  analyzing: number;
  total: number;
}

const Analysis: React.FC = () => {
  const { currentProject } = useProject();
  const orchestratorCtx = useOrchestratorContext();
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('1a. Document Summaries');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoStartedRef = useRef(false);

  // Global compliance score management
  const { complianceScore, calculateComplianceScore } = useComplianceScore();

  
const refreshAnalysisData = async () => {
  try {
    await loadAnalysisResults();
    console.log('Analysis data refreshed successfully');
  } catch (error) {
    console.error('Failed to refresh analysis data:', error);
  }
};

  const refreshAnalysisDataWithFeedback = async () => {
    setIsRefreshing(true);
    try {
      await refreshAnalysisData();
      setTimeout(() => setIsRefreshing(false), 500);
    } catch (error) {
      setIsRefreshing(false);
      toast.error('Failed to refresh data');
    }
  };

  const loadAnalysisProgress = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/api/analysis/progress/${currentProject.id}`);
      setAnalysisProgress(response.data);
    } catch (error) {
      console.error('Failed to load analysis progress');
    }
  }, [currentProject]);

 const loadAnalysisResults = useCallback(async () => {
  if (!currentProject) return;
  try {

    const timestamp = Date.now();
    const response = await api.get(`/api/analysis/results/${currentProject.id}?_t=${timestamp}`);
    
    console.log('Loaded fresh analysis results:', response.data);
    setAnalysisResults(response.data);

    // Calculate and update global compliance score
    const synthesisData = response.data.process_synthesis;
    calculateComplianceScore(synthesisData, response.data);
  } catch (error) {
    console.error('Failed to load analysis results:', error);
  }
}, [currentProject, calculateComplianceScore]);


  useEffect(() => {
    if (currentProject) {
      loadAnalysisProgress();
      loadAnalysisResults();
    }
  }, [currentProject]);

  // Poll for analysis results while analyzing
  useEffect(() => {
    let resultsInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing) {
      resultsInterval = setInterval(() => {
        loadAnalysisResults();
      }, 3000);
    }
    return () => {
      if (resultsInterval) clearInterval(resultsInterval);
    };
  }, [isAnalyzing, loadAnalysisResults]);

  // Poll for analysis PROGRESS while analyzing. Without this, analysisProgress
  // would stay frozen at whatever value was fetched on initial load — the
  // completion-detection effect below depends on analysisProgress changing
  // over time to flip isAnalyzing off. Missing polling here was the root
  // cause of the "zombie 1 of 2 analyzing" bug reported by the client.
  useEffect(() => {
    let progressInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing) {
      progressInterval = setInterval(() => {
        loadAnalysisProgress();
      }, 2000);
    }
    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isAnalyzing, loadAnalysisProgress]);

  // Timeout guard — even with progress polling, a silent worker crash or a
  // lost BullMQ job can leave documents in the 'analyzing' state forever on
  // the backend. In that case (completed + failed) never reaches total and
  // the UI would hang. Ten minutes is generous for normal 2-document
  // analysis (usually sub-3 minutes) but short enough that users aren't
  // left staring at a stuck spinner if something breaks.
  useEffect(() => {
    if (!isAnalyzing) return;
    const startedAt = Date.now();
    // 3 minutes per document, minimum 10 minutes, maximum 60 minutes
    const totalDocs = analysisProgress?.total ?? 3;
    const ANALYSIS_TIMEOUT_MS = Math.min(Math.max(totalDocs * 3 * 60 * 1000, 10 * 60 * 1000), 60 * 60 * 1000);
    console.log(`[Analysis] Timeout set to ${ANALYSIS_TIMEOUT_MS / 60000} min for ${totalDocs} docs`);
    const check = setInterval(() => {
      if (Date.now() - startedAt > ANALYSIS_TIMEOUT_MS) {
        console.warn('[Analysis] Analysis exceeded expected duration — exiting analyzing UI');
        toast.error(
          'Document analysis is taking longer than expected. Please retry or check backend logs.',
          { duration: 6000 },
        );
        setIsAnalyzing(false);
        loadAnalysisProgress();
        clearInterval(check);
      }
    }, 15000);
    return () => clearInterval(check);
  }, [isAnalyzing, loadAnalysisProgress, analysisProgress?.total]);

  // Stop analyzing only when NO docs are left in analyzing/pending state
  useEffect(() => {
    if (isAnalyzing && analysisProgress) {
      const { completed, failed, analyzing, pending, total } = analysisProgress;
      const allDone = analyzing === 0 && pending === 0 && total > 0;
      if (allDone) {
        setIsAnalyzing(false);
        loadAnalysisResults();
      }
    }
  }, [isAnalyzing, analysisProgress, loadAnalysisResults]);

  // Polling for process_synthesis and SOP after analysis completes
  useEffect(() => {
    // Exit if analysis is running or if we already have everything.
    if (isAnalyzing || (analysisResults?.process_synthesis && analysisResults?.sop)) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 40; // 2 minutes timeout

    const intervalId = setInterval(() => {
      if (attempts >= maxAttempts) {
        console.warn("Post-analysis polling timed out.");
        clearInterval(intervalId);
        return;
      }

      // Polling for synthesis/SOP attempt
      loadAnalysisResults();
      attempts++;
    }, 3000);

    return () => clearInterval(intervalId);
  }, [isAnalyzing, analysisResults, loadAnalysisResults]);

  const startAnalysis = async (fullAnalysis = false) => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }

    try {
      setIsAnalyzing(true);
      const response = await api.post(`/api/analysis/start/${currentProject.id}`, {
        fullAnalysis
      });
      toast.success(response.data.message);
      // Progress polling is handled by the useEffect that watches isAnalyzing.
      // No local interval needed here — that was causing duplicate polls and a
      // stale 10-minute timeout that fired even after proper completion.
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start analysis';
      toast.error(message);
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!orchestratorCtx.inRouter) return;
    if (!currentProject) return;
    if (!analysisProgress) return;
    if (isAnalyzing) return;
    if (analysisResults?.document_analyses && analysisResults.document_analyses.length > 0) return;

    const { total, completed, analyzing, pending } = analysisProgress;
    if (total === 0) return;
    if (completed > 0 || analyzing > 0) return;
    if (pending === 0) return;

    autoStartedRef.current = true;
    toast('Smart Router: starting document analysis automatically…', { icon: '🚀', duration: 3500 });
    startAnalysis(false);
  }, [orchestratorCtx.inRouter, currentProject, analysisProgress, isAnalyzing, analysisResults]);

  const reanalyzeCompleted = async () => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }

    try {
      setIsAnalyzing(true);
      const response = await api.post(`/api/analysis/start/${currentProject.id}`, {
        fullAnalysis: false,
        reanalyzeCompleted: true
      });
      toast.success(response.data.message || 'Re-analyzing completed documents...');
      // Progress polling handled by the useEffect that watches isAnalyzing.
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to start re-analysis';
      toast.error(message);
      setIsAnalyzing(false);
    }
  };


  const tabs = [
    { name: '1a. Document Summaries', icon: FileText },
    { name: '1b. Process Synthesis', icon: BarChart3 }
  ];

  const hasDocumentsButNoAnalysis = () => {
    const documents = analysisResults?.documents || [];
    const analyses = analysisResults?.document_analyses || [];
    return documents.length > 0 && analyses.length === 0;
  };

  const hasAnalysisButNoSynthesis = () => {
    const analyses = analysisResults?.document_analyses || [];
    const synthesis = analysisResults?.process_synthesis;
    return analyses.length > 0 && !synthesis;
  };

  const hasAnalysisAndSynthesisButNoSop = () => {
    const synthesis = analysisResults?.process_synthesis;
    const sop = analysisResults?.sop;
    return synthesis && !sop;
  };

  
  return (
    <div className="space-y-6">
      {/* Add refresh indicator */}
      {isRefreshing && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-3 py-2 rounded-md shadow-lg z-50">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span className="text-sm">Updating...</span>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h1 className="text-3xl font-bold text-super-dark-purple">Document Analysis Results</h1>
          <HelpTooltip
            title="Document Analysis Pipeline"
            content={
              <div className="space-y-2">
                <p>Jarvis uses a multi-stage AI pipeline to extract structured knowledge from your uploaded documents:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li><strong>Per-document analysis</strong> — each document is individually parsed and summarized with key entities, risks, and process steps extracted</li>
                  <li><strong>Cross-document synthesis</strong> — all individual analyses are merged into a unified process model with conflict resolution</li>
                  <li><strong>Terminology normalization</strong> — inconsistent terms across documents are reconciled into a canonical glossary</li>
                </ul>
                <p className="text-gray-500 italic">This isn't a simple summarizer — it builds a structured knowledge graph that powers downstream SOP generation, RACI matrices, and compliance checks.</p>
              </div>
            }
            variant="ai-explainer"
            iconSize={18}
          />
        </div>
        <div className="flex items-center space-x-3">
          <ToolbarDropdown
            label={isAnalyzing ? 'Analyzing...' : 'Analyze'}
            icon={isAnalyzing ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" /> : <Play className="w-4 h-4" />}
            dropdownPosition="bottom-right"
            headerHelp={{
              title: 'Document Analysis Pipeline',
              body: (
                <p>Choose which documents to analyze. New documents are processed incrementally; re-analysis regenerates everything from scratch for comprehensive integration.</p>
              ),
            }}
            items={[
              {
                key: 'analyze-new',
                label: 'Analyze New Documents',
                icon: <Play className="w-4 h-4" />,
                onClick: () => startAnalysis(false),
                disabled: isAnalyzing || (analysisProgress?.pending ?? 0) <= 0,
                loading: isAnalyzing,
                helpContent: {
                  title: 'Incremental Analysis',
                  body: (
                    <div>
                      <p>Only processes documents with "pending" status. Keeps existing results intact — faster execution for new documents only.</p>
                      <p className="mt-1 text-gray-500">Best for: adding new docs to an existing project.</p>
                    </div>
                  ),
                },
              },
              {
                key: 'reanalyze-completed',
                label: 'Re-analyze Completed',
                icon: <RefreshCw className="w-4 h-4" />,
                onClick: reanalyzeCompleted,
                disabled: isAnalyzing || (analysisProgress?.completed ?? 0) <= 0,
                loading: isAnalyzing,
                helpContent: {
                  title: 'Cache Reproducibility Test',
                  body: (
                    <div>
                      <p>Re-analyzes already-completed documents without deleting results. Tests LLM cache reproducibility — if working, results should be instant and identical.</p>
                      <p className="mt-1 text-gray-500">Best for: cache testing and verification.</p>
                    </div>
                  ),
                },
              },
              {
                key: 'reanalyze-everything',
                label: 'Re-analyze Everything',
                icon: <Wand2 className="w-4 h-4" />,
                onClick: () => startAnalysis(true),
                disabled: isAnalyzing || (analysisProgress?.completed ?? 0) <= 0,
                loading: isAnalyzing,
                helpContent: {
                  title: 'Full Regeneration',
                  body: (
                    <div>
                      <p>Resets ALL documents and reprocesses from scratch. Regenerates synthesis, SOPs, risk assessments — ensures comprehensive integration of all documents.</p>
                      <p className="mt-1 text-gray-500">Best for: when you want everything rebuilt.</p>
                    </div>
                  ),
                },
              },
            ]}
          />

          {analysisProgress && analysisProgress.analyzing > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                <span className="text-orange-800 text-xs font-medium">
                  Re-analyzing documents...
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project Context Display */}
      <ProjectContextDisplay />

      {analysisProgress && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Analysis Progress</h3>
            {(() => {
              // Processing is fully resolved when every doc finished or failed.
              // Previously this header kept reading "2/3 documents completed"
              // forever in the mixed-outcome case — the acceptance criteria
               // requires the UI to announce that processing is DONE even
              // when some docs errored, so we surface an explicit complete
              // state with the success / failure breakdown.
              const { completed, failed, total, analyzing } = analysisProgress;
              const isFullyResolved = total > 0 && (completed + failed) === total;
              if (isFullyResolved) {
                return (
                  <span className="text-sm font-medium text-green-700">
                    {failed > 0
                      ? `All processing complete — ${completed} succeeded, ${failed} failed`
                      : `All processing complete — ${completed} succeeded`}
                  </span>
                );
              }
              return (
                <span className="text-sm text-gray-600">
                  {completed}/{total} documents completed
                  {analyzing > 0 && ` • ${analyzing} analyzing`}
                </span>
              );
            })()}
          </div>

          {/*
           * Segmented progress bar — green for succeeded, amber for currently
           * analyzing, red for failed. The old implementation used a single
           * blue bar tied to `analysisProgress.progress` which only counted
           * completed docs; in a 2-succeeded / 1-failed run that left the bar
           * frozen at ~66% even though the pipeline was fully finished, which
           * looked like the UI was stuck mid-process. Stacking the segments
           * lets the bar reach 100% when all docs have resolved (succeeded
           * OR failed) while still visually communicating the failure slice.
           */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden flex">
            {(() => {
              const { completed, failed, analyzing, total } = analysisProgress;
              if (!total || total <= 0) return null;
              const pct = (n: number) => Math.max(0, Math.min(100, (n / total) * 100));
              return (
                <>
                  {completed > 0 && (
                    <div
                      className="bg-green-500 h-2 transition-all duration-300"
                      style={{ width: `${pct(completed)}%` }}
                    />
                  )}
                  {analyzing > 0 && (
                    <div
                      className="bg-electric-blue h-2 transition-all duration-300"
                      style={{ width: `${pct(analyzing)}%` }}
                    />
                  )}
                  {failed > 0 && (
                    <div
                      className="bg-red-500 h-2 transition-all duration-300"
                      style={{ width: `${pct(failed)}%` }}
                    />
                  )}
                </>
              );
            })()}
          </div>

          <div className="grid grid-cols-5 gap-4 text-center text-sm">
            <div>
              <div className="text-2xl font-bold text-green-600">{analysisProgress.completed}</div>
              <div className="text-gray-600">Completed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{analysisProgress.analyzing || 0}</div>
              <div className="text-gray-600">Analyzing</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{analysisProgress.pending}</div>
              <div className="text-gray-600">Pending</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{analysisProgress.failed}</div>
              <div className="text-gray-600">Failed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-600">{analysisProgress.total}</div>
              <div className="text-gray-600">Total</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2 ${activeTab === tab.name
                    ? 'border-electric-blue text-electric-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === '1a. Document Summaries' && (
            <AnalysisResults
              analysisResults={analysisResults}
              isAnalyzing={isAnalyzing}
              analysisProgress={analysisProgress}
              setActiveTab={setActiveTab}
               onStartAnalysis={startAnalysis}
            />
          )}
          {activeTab === '1b. Process Synthesis' && (
            <EnhancedProcessSynthesisTab
              analysisResults={analysisResults}
              isAnalyzing={isAnalyzing}
              analysisProgress={analysisProgress}
              globalComplianceScore={complianceScore}
              onRefresh={refreshAnalysisDataWithFeedback}
            />
          )}
        </div>
      </div>

    </div>
  );
};

export default Analysis;